import { useSyncExternalStore } from 'react';

/**
 * Privacidade das conversas (T2.5.1). Por padrão ninguém vê quando você está
 * online; confirmação de leitura e "digitando" seguem ligados e podem ser
 * desligados em Ajustes. Fica em `profile.preferences.privacy` e num espelho
 * local para valer desde a abertura do mensageiro.
 */
export type ChatPrivacy = { readReceipts: boolean; typing: boolean; presence: boolean };

export const DEFAULT_PRIVACY: ChatPrivacy = { readReceipts: true, typing: true, presence: false };
const STORAGE_KEY = 'secretario.privacy';

function load(): ChatPrivacy {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw
      ? { ...DEFAULT_PRIVACY, ...(JSON.parse(raw) as Partial<ChatPrivacy>) }
      : DEFAULT_PRIVACY;
  } catch {
    return DEFAULT_PRIVACY;
  }
}

let current: ChatPrivacy = load();
const listeners = new Set<() => void>();

export function getPrivacy(): ChatPrivacy {
  return current;
}

export function parsePrivacy(value: unknown): ChatPrivacy {
  const raw = (value && typeof value === 'object' ? value : {}) as Partial<ChatPrivacy>;
  return {
    readReceipts: raw.readReceipts ?? DEFAULT_PRIVACY.readReceipts,
    typing: raw.typing ?? DEFAULT_PRIVACY.typing,
    presence: raw.presence ?? DEFAULT_PRIVACY.presence,
  };
}

export function setPrivacy(next: ChatPrivacy): void {
  if (
    next.readReceipts === current.readReceipts &&
    next.typing === current.typing &&
    next.presence === current.presence
  )
    return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* sem espaço: vale só nesta sessão */
  }
  for (const listener of listeners) listener();
}

export function subscribePrivacy(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePrivacy(): ChatPrivacy {
  return useSyncExternalStore(
    subscribePrivacy,
    () => current,
    () => current,
  );
}
